import { PublicClientApplication } from '@azure/msal-browser'
import { getSelectedEnvKey } from 'src/config/environments'

/**
 * Per-environment authentication.
 *
 * Each backend environment validates access tokens against its OWN Entra
 * tenant, so a token minted for one tenant is rejected ("Token is invalid")
 * by a backend in another tenant. The admin therefore needs a token from the
 * tenant that matches the selected backend.
 *
 * Backends map to two auth "realms" (one Entra tenant each):
 *   - develop + staging -> nonprod tenant (dashusers0)
 *   - production        -> prod tenant   (dashusersprod0)
 *
 * Switching environment triggers a full page reload (see EnvironmentSelector),
 * so we only need to configure MSAL for the currently selected environment at
 * load time. Each realm uses its own app registration (clientId) — MSAL binds
 * one clientId per instance — and MSAL keeps a separate account cache per
 * clientId, so the admin logs in once per realm (typically twice: non-prod and
 * prod) and each stays signed in across reloads.
 *
 * Every value can be overridden via env vars (per realm), but sensible
 * defaults are baked in so no extra secret is required for the known tenants.
 */
const REALM_BY_ENV = {
  develop: 'nonprod',
  staging: 'nonprod',
  production: 'prod',
}

const REALMS = {
  nonprod: {
    // "user-manager" app in the dashusers0 tenant
    clientId: process.env.REACT_APP_CLIENT_ID_NONPROD || '972dfe00-87a2-4f77-9dd3-cc18853b8e8e',
    authority:
      process.env.REACT_APP_AUTHORITY_NONPROD ||
      'https://login.microsoftonline.com/2486a5b9-06c2-4dc6-8c5a-621091da2157',
    apiScope:
      process.env.REACT_APP_API_SCOPE_NONPROD ||
      'api://29816f78-6b35-42d1-890b-a1ceb5165911/read',
  },
  prod: {
    // "user-manager" app in the dashusersprod0 tenant
    clientId: process.env.REACT_APP_CLIENT_ID_PROD || '89a1e6c1-1a29-4764-b4c7-4af4410fc775',
    authority:
      process.env.REACT_APP_AUTHORITY_PROD ||
      'https://login.microsoftonline.com/ba5dd51b-ae8b-4657-826b-c02dc7b8db50',
    apiScope:
      process.env.REACT_APP_API_SCOPE_PROD ||
      'api://0f784c94-ffcd-496d-957a-9f79f2005e6e/read',
  },
}

const selectedEnv = getSelectedEnvKey()
export const activeRealmKey = REALM_BY_ENV[selectedEnv] || 'prod'
const realm = REALMS[activeRealmKey]

// Tenant (GUID) of the active realm. The MSAL cache is shared across realms on
// the same origin, so an account from another realm/tenant would otherwise be
// counted as "signed in" here. Auth checks must be scoped to this tenant.
export const activeRealmTenantId =
  realm.authority.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0] || ''

export const accountMatchesRealm = (account) =>
  !!account &&
  (account.tenantId === activeRealmTenantId ||
    account.idTokenClaims?.tid === activeRealmTenantId)

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: realm.clientId,
    authority: realm.authority,
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
})

const baseScopes = ['openid', 'profile', 'email']

// Used by the axios interceptor to silently fetch the API-audience token for
// the selected environment's tenant.
export const apiRequest = {
  scopes: realm.apiScope ? [realm.apiScope] : baseScopes,
}

// Login requests the API scope up front so the API-audience token is cached
// (the app is pre-authorized for it, so there is no extra consent prompt).
export const loginRequest = {
  scopes: realm.apiScope ? [...baseScopes, realm.apiScope] : baseScopes,
  prompt: 'login',
}

/**
 * MSAL v4 requires explicit initialization before any interaction.
 * This promise is awaited in index.js before rendering the app
 * to prevent race conditions with useIsAuthenticated() during startup.
 *
 * After initialization, restore the active account from cache so that
 * useIsAuthenticated() returns true immediately on page reload
 * instead of requiring a fresh login.
 */
export const msalInitPromise = msalInstance.initialize().then(() => {
  // Only treat an account from the CURRENT realm/tenant as signed in. The MSAL
  // cache is shared across realms on the same origin, so without this filter an
  // account from another environment would be picked as active and the app
  // would appear "logged in" while every API call fails (wrong tenant).
  const match = msalInstance.getAllAccounts().find(accountMatchesRealm)
  msalInstance.setActiveAccount(match || null)
})
